import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Icon,
    Typography,
} from '@mui/material'
import { ReactElement, useCallback, useMemo } from 'react'
import './Navbar.scss'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import {
    changeOwnPassword,
    updateUserUnits,
} from '../../../services/userServices'
import { getUserData } from '../../../redux/slices/userSlice'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import { useUnits } from '../../../hooks/useUnits'
import UnitsPreferences from './UnitsPreferences'
import LocalizationPreferences from './LocalizationPreferences'
import { useLocalization } from '../../../hooks/useLocalization'
import { defaultLocalization } from '../../../utils/DateAndTimeUtils'
import { useTranslation } from 'react-i18next'
import i18n from '../../../i18n'
import PaperComponent from '../../Common/PaperComponent'
import PopupTitle from '../../Common/PopupTitle'

interface IUserDetailsProps {
    openDialog: boolean
    handleClose: () => void
}

const UserProfileDetails = (props: IUserDetailsProps): ReactElement => {
    const { t } = useTranslation()
    const { openDialog, handleClose } = props
    const user = useAppSelector((state) => state.user.user)
    const unitsList = useAppSelector((state) => state.user.unitsList)
    const orgId = useAppSelector((state) => state.org.org?.id || '')
    const dispatch = useAppDispatch()

    const handleResetPassword = useCallback(async () => {
        dispatch(updateLoader(true))
        try {
            await changeOwnPassword()
        } finally {
            dispatch(updateLoader(false))
        }
    }, [dispatch])

    const initialUnits = useMemo(() => {
        if (user && unitsList?.length) {
            const userUnits = user?.preferences?.units
            return unitsList?.map((i) => ({
                ...i,
                selected: userUnits ? userUnits[i.id] || '' : '',
            }))
        }
        return []
    }, [unitsList, user])

    const { units, onChangeUnits, isUnitsUpdated } = useUnits(initialUnits)

    // set initial localization values
    const initialValues = useMemo(() => {
        if (user) {
            return {
                ...defaultLocalization,
                ...user.preferences?.localization,
                language:
                    user.preferences?.localization?.language ||
                    defaultLocalization.language,
            }
        }

        return defaultLocalization
    }, [user])

    const { localization, handleLocalizationChange, isLocalizationUpdated } =
        useLocalization(initialValues)

    const isDirty = isUnitsUpdated || isLocalizationUpdated

    const onSaveUserData = async () => {
        dispatch(updateLoader(true))
        const updatedUints: { [id: string]: string } = {}
        units?.forEach((i) => {
            updatedUints[i.id] = i.selected || ''
        })
        const payload = {
            units: updatedUints,
            localization,
        }
        await updateUserUnits(payload, user?.id || '')
        if (localization.language && i18n.language !== localization.language) {
            await i18n.changeLanguage(localization.language)
        }
        handleClose()
        dispatch(getUserData({ orgId, email: user?.email || '' }))
        dispatch(updateLoader(false))
    }

    const handleCloseDialog = () => {
        handleClose()
    }

    return (
        <div>
            <Dialog
                aria-labelledby="customized-dialog-title"
                open={openDialog}
                PaperProps={{
                    component: 'form',
                    onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                        event.preventDefault()
                        handleClose()
                    },
                }}
                PaperComponent={PaperComponent}
                maxWidth="lg"
                fullWidth
                aria-modal="true"
                role="dialog"
            >
                {/* UBQ-5844: shared title row; the theme sets the title size (was a 20px override).
                    The h2 is the drag handle, the inner span keeps the aria-labelledby target. */}
                <PopupTitle
                    id="draggable-dialog-title"
                    draggable
                    onClose={handleClose}
                    closeButtonId="close-icon"
                >
                    <span id="customized-dialog-title">{t('userDetails')}</span>
                </PopupTitle>
                <DialogContent dividers>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                            }}
                        >
                            {user?.picture ? (
                                <Box className="imageContainer">
                                    <img src={user?.picture} alt="User" />
                                </Box>
                            ) : (
                                <Icon sx={{ width: '100px', height: '100px' }}>
                                    <AccountCircleIcon
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            color: '#00000070',
                                        }}
                                    />
                                </Icon>
                            )}

                            <Box
                                sx={{
                                    marginLeft: '25px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#0009',
                                            fontSize: '16px',
                                            textWrap: 'nowrap',
                                        }}
                                    >
                                        {t('emailLabel')}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: '#000',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            textWrap: 'nowrap',
                                            marginLeft: '4px',
                                        }}
                                    >
                                        {user?.email || ''}
                                    </Typography>
                                </Box>
                            </Box>
                            <Button
                                id="reset-own-password-button"
                                onClick={handleResetPassword}
                                variant="outlined"
                                aria-label={t('resetPassword')}
                                title={t('resetPassword')}
                                sx={{
                                    marginLeft: 'auto',
                                    alignSelf: 'center',
                                    textWrap: 'nowrap',
                                }}
                            >
                                {t('resetPassword')}
                            </Button>
                        </Box>
                        <LocalizationPreferences
                            initialPreferences={localization}
                            handleLocalizationChange={handleLocalizationChange}
                        />
                        <UnitsPreferences
                            units={units}
                            onChangeUnits={onChangeUnits}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseDialog}
                        id="user-profile-close"
                        aria-label={t('close') || 'Close'}
                        title={t('close') || 'Close'}
                    >
                        {t('close')}
                    </Button>
                    <Button
                        onClick={() => onSaveUserData()}
                        id="user-profile-save"
                        disabled={!isDirty}
                        aria-label={t('save') || 'Save'}
                        title={t('save') || 'Save'}
                    >
                        {t('save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default UserProfileDetails
